import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import $ from 'jquery';
import { formatCurrency } from '@/js/shared/billables/currency';
import { taxRate } from '@/js/shared/billables/decimal';

const EMPTY_RATE = 'select_a_sales_tax';
const ADD_RATE = 'add_sales_tax';

export default class extends Controller {
  static values = { newSalesTaxPath: String };

  static targets = [
    'rateSelect',
    'percentage',
    'calculatedAmount',
    'shippingContainer',
    'shippingCheckbox',
    'allItemsCheckbox',
  ];

  static outlets = ['billable-item'];

  get calculationInput() {
    return {
      rate: this.rateSelectTarget.selectedOptions[0]?.dataset.taxRate || '0',
      taxShipping: this.shippingCheckboxTarget.checked,
    };
  }

  get allItemsTaxable() {
    return this.#activeItemOutlets.every((item) => item.taxable);
  }

  rateSelectTargetConnected(element) {
    this.rateOptionsObserver = new MutationObserver(() => {
      if (element.value !== ADD_RATE) {
        this.#applySelectedRate();
      }
    });
    this.rateOptionsObserver.observe(element, { childList: true });
  }

  rateSelectTargetDisconnected() {
    this.rateOptionsObserver?.disconnect();
  }

  async changeRate(event) {
    event.stopPropagation();

    if (this.rateSelectTarget.value === ADD_RATE) {
      await this.#openSalesTaxModal();
      return;
    }

    this.#applySelectedRate();
  }

  #applySelectedRate() {
    const taxable = this.rateSelectTarget.value !== EMPTY_RATE;
    this.#activeItemOutlets.forEach((item) => {
      item.taxable = taxable;
    });
    this.#syncAllItemsCheckbox();
    this.#syncRateDisplay();
    this.#broadcastChanged();
  }

  itemChanged() {
    this.#syncTaxState();
  }

  itemRemoved() {
    this.#syncTaxState();
  }

  toggleAllItems() {
    const taxable = this.allItemsCheckboxTarget.checked;
    this.#activeItemOutlets.forEach((item) => {
      item.taxable = taxable;
    });

    this.#syncRateDisplay();
    this.#broadcastChanged();
  }

  update() {
    this.#broadcastChanged();
  }

  renderCalculatedAmount({ tax, currency }) {
    this.calculatedAmountTarget.textContent = formatCurrency(tax, currency, { useGrouping: false });
  }

  get #activeItemOutlets() {
    return this.billableItemOutlets.filter((item) => !item.markedForDestruction);
  }

  #syncTaxState() {
    this.#syncAllItemsCheckbox();
    this.#syncRateDisplay();
    this.#broadcastChanged();
  }

  #syncAllItemsCheckbox() {
    if (this.hasAllItemsCheckboxTarget) {
      this.allItemsCheckboxTarget.checked = this.allItemsTaxable;
    }
  }

  #syncRateDisplay() {
    const rate = this.calculationInput.rate;
    const selected = ![EMPTY_RATE, ADD_RATE].includes(this.rateSelectTarget.value);

    this.percentageTarget.textContent = `${taxRate(rate)}%`;
    this.shippingContainerTarget.classList.toggle('hide', !selected);
  }

  #broadcastChanged() {
    this.dispatch('changed');
  }

  async #openSalesTaxModal() {
    if (!this.hasNewSalesTaxPathValue) {
      return;
    }

    const request = new FetchRequest('get', this.newSalesTaxPathValue, {
      responseKind: 'html',
    });
    const response = await request.perform();

    if (response.ok) {
      const html = await response.html;
      $(html).appendTo('body').modal();
    }
  }
}
